<template>
    <div class="page-contents">
        <component
            :is="globalStore.header.component"
            v-if="globalStore && globalStore.header"
            :global="globalStore.global.content"
            :blok="globalStore.header"
        />
        <main>
            <component v-if="story" :is="story.content.component" :blok="story.content" />
        </main>
        <component
            :is="'Lazy' + globalStore.emailSubscription.component"
            v-if="globalStore && globalStore.emailSubscription"
            :global="globalStore.global.content"
            :blok="globalStore.emailSubscription"
        />
        <component
            :is="'Lazy' + globalStore.footer.component"
            v-if="globalStore && globalStore.footer"
            :global="globalStore.global.content"
            :blok="globalStore.footer"
        />
    </div>
</template>

<script setup lang="ts">
import { useGlobalStore } from '@nuxt-monorepo/base/app/store/global';
import type { IShuttleLocationData } from '@nuxt-monorepo/ui/types/products/ShuttleProducts';
import type { RouteLocationNormalizedLoaded } from 'vue-router';

definePageMeta({
    path: '/:airport([A-Za-z]{3})/l/:location',
});

// Constants
const config = useRuntimeConfig();
const globalStore: any = useGlobalStore();
const storyblokApi = useStoryblokApi();
const { locale } = useI18n();
const route: RouteLocationNormalizedLoaded = useRoute();
const airportSlug = Array.isArray(route.params.airport) ? route.params.airport[0] : route.params.airport || '';
const productSlug = Array.isArray(route.params.location) ? route.params.location[0] : route.params.location || '';
const pageSlug = `${config?.public.STORYBLOK_FOLDER}/pages/airports/${airportSlug}/location/${productSlug}`;
const templateSlug = `${config?.public.STORYBLOK_FOLDER}/pages/airports/shuttle-journey-template`;
const resolveRelations = [
    'CoreSharedComponent.SharedComponent',
    'Page.globals',
    'ImageGridAirports.Airports',
    'ImageGridAirportsV2.Airports',
    'ImageGrid.Airports',
    'Link_Section.entities',
    'PSFAirportPage.globals',
    'PSFAirportPage.AirportEntity',
    'PartnersContainer.Partners',
];

// Helpers

/** Returns true if route should be blocked by config */
function isBlockedRoute(config: any, route: RouteLocationNormalizedLoaded): boolean {
    const blocked = config?.public.STORYBLOK_ROUTE_BLOCKED;
    if (!blocked) return false;
    return blocked.split(', ').some((block: string) => route.path.includes(block));
}

/** Returns object for deep replacements in storyblok content */
function getReplacements(locationData: IShuttleLocationData | null) {
    return {
        airportCode: locationData?.airport?.code ?? '',
        airportName: locationData?.airport?.name ?? '',
        airportSlug: airportSlug ?? '',
        locationName: locationData?.dropoff ?? locationData?.pickup ?? '',
        locationSlug: productSlug ?? '',
    };
}

/** Fetches a Storyblok story by slug */
async function fetchStory(slug: string) {
    return storyblokApi
        .get(`cdn/stories/${slug}`, {
            version: config.public.STORYBLOK_VERSION === 'published' ? 'published' : 'draft',
            language: locale.value,
            resolve_relations: resolveRelations,
        })
        .then((res) => res?.data?.story || null)
        .catch(() => null);
}

// ---- MAIN LOGIC ----

// Get airport code
if (!airportSlug || !productSlug) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

// Get location data
const { data: locationDataRaw } = await useAsyncData<{ data: IShuttleLocationData }>(
    `${airportSlug.replace(/-/g, '_')}/l/${productSlug}`,
    () =>
        $privateApi(`v1/${airportSlug.replace(/-/g, '_')}/l/${productSlug}`, {
            method: 'GET',
        }),
    { lazy: true }
);
const locationData = locationDataRaw.value?.data || null;
provide('locationData', locationData);

// Get locations data
const { data: airportLocationsDataRaw } = await useAsyncData<{ data: IShuttleLocationData[] }>(
    `${airportSlug.replace(/-/g, '_')}/l`,
    () =>
        $privateApi(`v1/${airportSlug.replace(/-/g, '_')}/l?prefix=${productSlug.charAt(0)}`, {
            method: 'GET',
        }),
    { lazy: true }
);
const shuttleLocationsList = airportLocationsDataRaw.value?.data || null;
provide('shuttleLocationsList', shuttleLocationsList);

// Load Storyblok content
const storyData = (await fetchStory(pageSlug)) || (await fetchStory(templateSlug));

const story = ref(storyData);

// Blocked route or no content → error
if (isBlockedRoute(config, route) || !story?.value || !locationData?.slug) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

// Replace placeholders in Storyblok content
const replacements = getReplacements(locationData);
story.value.content = deepReplacePlaceholders(story.value.content, replacements);

// Handle SEO/meta and global store
if (story?.value) {
    setSeoMeta(story.value.content?.seo, config?.public, route);
    if (story.value.content?.globals) {
        globalStore.update(story.value.content.globals);
    }
}
</script>
